import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/* ═══════════════════════════════════════════════════════════════
   useNewbornRecord — Custom hook for Newborn Record & APGAR (Task 12)
   Manages newborn assessment, APGAR scoring, immediate care checklist,
   birth vaccinations, and outcome recording.
   ═══════════════════════════════════════════════════════════════ */

const USE_MOCK_DATA = false;

/* ─── APGAR Scoring Criteria ─── */
const APGAR_CRITERIA = [
    {
        key: "appearance",
        label: "Appearance (Skin colour)",
        options: [
            { score: 0, label: "Blue/pale all over" },
            { score: 1, label: "Body pink, extremities blue" },
            { score: 2, label: "Completely pink" },
        ],
    },
    {
        key: "pulse",
        label: "Pulse (Heart rate)",
        options: [
            { score: 0, label: "Absent" },
            { score: 1, label: "Below 100 bpm" },
            { score: 2, label: "100 bpm or above" },
        ],
    },
    {
        key: "grimace",
        label: "Grimace (Reflex irritability)",
        options: [
            { score: 0, label: "No response" },
            { score: 1, label: "Grimace/weak cry" },
            { score: 2, label: "Vigorous cry/cough/sneeze" },
        ],
    },
    {
        key: "activity",
        label: "Activity (Muscle tone)",
        options: [
            { score: 0, label: "Limp/flaccid" },
            { score: 1, label: "Some flexion" },
            { score: 2, label: "Active movement" },
        ],
    },
    {
        key: "respiration",
        label: "Respiration (Breathing)",
        options: [
            { score: 0, label: "Absent" },
            { score: 1, label: "Slow/irregular" },
            { score: 2, label: "Good/crying" },
        ],
    },
];

/* ─── Constants ─── */
const SEX_OPTIONS = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "ambiguous", label: "Ambiguous" },
];

const RESUSCITATION_TYPES = [
    { value: "stimulation", label: "Stimulation" },
    { value: "suction", label: "Suction" },
    { value: "bag_mask", label: "Bag & Mask Ventilation" },
    { value: "intubation", label: "Intubation" },
    { value: "chest_compressions", label: "Chest Compressions" },
];

const CORD_CARE_OPTIONS = [
    { value: "chlorhexidine", label: "Chlorhexidine 4%" },
    { value: "dry_care", label: "Dry Cord Care" },
];

const OUTCOME_OPTIONS = [
    { value: "alive", label: "Live Birth — Alive" },
    { value: "fresh_stillbirth", label: "Fresh Stillbirth" },
    { value: "macerated_stillbirth", label: "Macerated Stillbirth" },
    { value: "neonatal_death", label: "Early Neonatal Death" },
];

const FEEDING_OPTIONS = [
    { value: "exclusive_breastfeeding", label: "Exclusive Breastfeeding" },
    { value: "replacement_feeding", label: "Replacement Feeding" },
    { value: "mixed", label: "Mixed Feeding" },
];

/* ─── Helper ─── */
const nowLocal = function() {
    const d = new Date();
    const pad = function(n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
        "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
};

const notify = function(actions, variant, message) {
    try {
        const sa = actions.snackbarActions;
       
        if (typeof sa.snackbarOpen === "function") {
            sa.snackbarOpen(variant, message);
        } else if (typeof sa.openSnackbar === "function") {
            sa.openSnackbar(variant, message);
        } else if (typeof sa.showSnackbar === "function") {
            sa.showSnackbar(message, variant);
        } else {
        
            console.warn("[useNewbornRecord] No snackbar action found. variant=%s message=%s", variant, message);
        }
    } catch (err) {
        console.error("[useNewbornRecord] notify() threw:", err);
    }
};

/* ─── Initial Form ─── */
const INITIAL_FORM = {
    birth_order: 1,
    sex: "",
    birth_weight_grams: "",
    birth_length_cm: "",
    head_circumference_cm: "",
    resuscitation_needed: false,
    resuscitation_type: [],
    skin_to_skin_initiated: false,
    skin_to_skin_time: "",
    breastfeeding_within_1hr: false,
    breastfeeding_time: "",
    vitamin_k_given: false,
    eye_prophylaxis_given: false,
    cord_care: "chlorhexidine",
    bcg_given: false,
    opv0_given: false,
    hep_b_birth_dose: false,
    nevirapine_given: false,
    congenital_anomalies: "",
    outcome: "alive",
    death_time: "",
    physical_exam_notes: "",
    feeding_option: "exclusive_breastfeeding",
};

/* ─── Initial APGAR state ─── */
const INITIAL_APGAR = {
    "1min": { appearance: null, pulse: null, grimace: null, activity: null, respiration: null },
    "5min": { appearance: null, pulse: null, grimace: null, activity: null, respiration: null },
    "10min": { appearance: null, pulse: null, grimace: null, activity: null, respiration: null },
};

/* ═══ MOCK DATA ═══ */
const MOCK_DELIVERY = {
    id: 6001,
    labour_admission_id: 9001,
    booking_id: 3001,
    delivery_datetime: nowLocal(),
    delivery_mode: "svd",
    number_of_babies: 1,
    estimated_blood_loss_ml: 250,
    maternal_condition_post_delivery: "stable",
};

const MOCK_NEWBORNS = [];

/* ═══ HOOK ═══ */
export const useNewbornRecord = function(actions, patient_number, visit_id, onSaveSuccess) {

    /* ─── Delivery record ─── */
    const delState = useState(null);
    const deliveryRecord = delState[0];
    const setDeliveryRecord = delState[1];

    /* ─── Existing newborn records ─── */
    const nbListState = useState([]);
    const newborns = nbListState[0];
    const setNewborns = nbListState[1];

    /* ─── Current newborn form ─── */
    const formState = useState(Object.assign({}, INITIAL_FORM));
    const form = formState[0];
    const setForm = formState[1];

    /* ─── APGAR scores ─── */
    const apgarState = useState(JSON.parse(JSON.stringify(INITIAL_APGAR)));
    const apgar = apgarState[0];
    const setApgar = apgarState[1];

    /* ─── Editing ─── */
    const editState = useState(null);
    const editingNewborn = editState[0];
    const setEditingNewborn = editState[1];

    /* ─── UI ─── */
    const loadState = useState(true);
    const loading = loadState[0];
    const setLoading = loadState[1];

    const savingState = useState(false);
    const saving = savingState[0];
    const setSaving = savingState[1];

    const tabState = useState("assessment");
    const activeTab = tabState[0];
    const setActiveTab = tabState[1];

    /* ─── Fetch delivery + existing newborns ─── */
    useEffect(function() {
        if (!patient_number || !visit_id) {
            setLoading(false);
            return;
        }
        setLoading(true);

        if (USE_MOCK_DATA) {
            setTimeout(function() {
                setDeliveryRecord(MOCK_DELIVERY);
                setNewborns(MOCK_NEWBORNS);
                setLoading(false);
            }, 400);
            return;
        }

        /* Helper: fetch newborns for a given delivery id */
        const fetchNewborns = function(deliveryId) {
            return axios
                .get(config.smsUrl + "/obgyn/newborns/" + deliveryId)
                .then(function(res) { return Array.isArray(res.data) ? res.data : []; })
                .catch(function(err) {
                    console.warn("[useNewbornRecord] fetchNewborns failed:", err);
                    return [];
                });
        };

        /* Helper: fetch delivery by labour admission id */
        const fetchDeliveryByAdmission = function(admissionId) {
            if (!admissionId) return Promise.resolve(null);
            return axios
                .get(config.smsUrl + "/obgyn/delivery/by-admission/" + admissionId)
                .then(function(res) {
                    const d = res.data || null;
                    return d && d.id ? d : null;
                })
                .catch(function(err) {
                    console.warn("[useNewbornRecord] fetchDeliveryByAdmission failed:", err);
                    return null;
                });
        };

        /* Primary path: delivery by visit */
        axios
            .get(config.smsUrl + "/obgyn/delivery/by-visit/" + visit_id)
            .then(function(res) {
                const data = res.data || {};
                if (data && data.id) {
                    setDeliveryRecord(data);
                    return fetchNewborns(data.id);
                }

                /* Fallback: look up via labour admission */
                return axios
                    .get(config.smsUrl + "/obgyn/labour/admission-by-visit/" + visit_id)
                    .then(function(res2) {
                        const admission = res2.data || {};
                        return fetchDeliveryByAdmission(admission.id || null);
                    })
                    .then(function(delivery) {
                        if (delivery && delivery.id) {
                            setDeliveryRecord(delivery);
                            return fetchNewborns(delivery.id);
                        }
                        /* No delivery found — leave deliveryRecord as null */
                        console.warn("[useNewbornRecord] No delivery record found for visit:", visit_id);
                        return [];
                    });
            })
            .then(function(babies) {
                setNewborns(babies);
            })
            .catch(function(err) {
                console.error("[useNewbornRecord] Initial fetch error:", err);
            })
            .finally(function() {
                setLoading(false);
            });
    }, [patient_number, visit_id]);

    /* ─── Form handlers ─── */
    const handleChange = useCallback(function(e) {
        const name = e.target.name;
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm(function(prev) {
            return Object.assign({}, prev, { [name]: value });
        });
    }, []);

    /* ─── APGAR score handler ─── */
    const setApgarScore = useCallback(function(minute, criterion, score) {
        setApgar(function(prev) {
            const next = JSON.parse(JSON.stringify(prev));
            next[minute][criterion] = score;
            return next;
        });
    }, []);

    /* ─── Compute APGAR totals ─── */
    const apgarTotals = useMemo(function() {
        const result = {};
        ["1min", "5min", "10min"].forEach(function(m) {
            const scores = apgar[m];
            let total = 0;
            let complete = true;
            APGAR_CRITERIA.forEach(function(c) {
                if (scores[c.key] === null || scores[c.key] === undefined) {
                    complete = false;
                } else {
                    total += scores[c.key];
                }
            });
            result[m] = { total: total, complete: complete };
        });
        return result;
    }, [apgar]);

    /* ─── APGAR color / label classification ─── */
    const getApgarColor = useCallback(function(score) {
        if (score >= 7) return "#2e7d32";
        if (score >= 4) return "#f57f17";
        return "#c62828";
    }, []);

    const getApgarLabel = useCallback(function(score) {
        if (score >= 7) return "Normal";
        if (score >= 4) return "Moderate asphyxia";
        return "Severe asphyxia";
    }, []);

    /* ─── Toggle resuscitation type ─── */
    const toggleResuscitationType = useCallback(function(type) {
        setForm(function(prev) {
            const current = prev.resuscitation_type || [];
            const idx = current.indexOf(type);
            const next = idx >= 0
                ? current.filter(function(t) { return t !== type; })
                : current.concat([type]);
            return Object.assign({}, prev, { resuscitation_type: next });
        });
    }, []);

    /* ─── Immediate care items ─── */
    const immediateCareItems = useMemo(function() {
        return [
            { key: "skin_to_skin_initiated",   label: "Skin-to-Skin Contact",          icon: "🤱", checked: form.skin_to_skin_initiated },
            { key: "breastfeeding_within_1hr", label: "Breastfeeding within 1 hour",   icon: "🍼", checked: form.breastfeeding_within_1hr },
            { key: "vitamin_k_given",           label: "Vitamin K administered",        icon: "💊", checked: form.vitamin_k_given },
            { key: "eye_prophylaxis_given",     label: "Eye prophylaxis (TTC)",         icon: "👁️", checked: form.eye_prophylaxis_given },
        ];
    }, [form]);

    /* ─── Vaccination items ─── */
    const vaccinationItems = useMemo(function() {
        return [
            { key: "bcg_given",        label: "BCG Vaccine",                icon: "💉", checked: form.bcg_given },
            { key: "opv0_given",       label: "OPV-0 (Oral Polio Vaccine)", icon: "💉", checked: form.opv0_given },
            { key: "hep_b_birth_dose", label: "Hepatitis B Birth Dose",     icon: "💉", checked: form.hep_b_birth_dose },
            { key: "nevirapine_given", label: "Nevirapine (HIV-exposed)",   icon: "💊", checked: form.nevirapine_given },
        ];
    }, [form]);

    /* ─── Birth weight classification ─── */
    const weightClass = useMemo(function() {
        const w = parseFloat(form.birth_weight_grams);
        if (!w) return null;
        if (w < 1000) return { label: "Extremely Low Birth Weight", color: "#c62828" };
        if (w < 1500) return { label: "Very Low Birth Weight",      color: "#c62828" };
        if (w < 2500) return { label: "Low Birth Weight",           color: "#f57f17" };
        if (w <= 4000) return { label: "Normal Birth Weight",       color: "#2e7d32" };
        return          { label: "Macrosomia (Large Baby)",          color: "#f57f17" };
    }, [form.birth_weight_grams]);

    /* ─── Save newborn ─── */
    const saveNewborn = useCallback(function() {
        /* ── Validation ── */
        if (!form.sex) {
            notify(actions, "error", "Please select the baby's sex");
            return;
        }
        if (!form.birth_weight_grams) {
            notify(actions, "error", "Please enter birth weight");
            return;
        }
        if (!deliveryRecord || !deliveryRecord.id) {
            notify(actions, "error", "Delivery record not found. Please save the delivery record first.");
            console.error("[useNewbornRecord] saveNewborn blocked — deliveryRecord is:", deliveryRecord);
            return;
        }

        setSaving(true);

        const payload = Object.assign({}, form, {
            delivery_id:           deliveryRecord.id,
            mother_patient_number: patient_number,
            apgar_1min:  apgarTotals["1min"].complete  ? apgarTotals["1min"].total  : null,
            apgar_5min:  apgarTotals["5min"].complete  ? apgarTotals["5min"].total  : null,
            apgar_10min: apgarTotals["10min"].complete ? apgarTotals["10min"].total : null,
            apgar_details: apgar,
        });

        /* ── Mock path ── */
        if (USE_MOCK_DATA) {
            setTimeout(function() {
                const mockPairData = payload.outcome === "alive" ? {
                    baby_patient_number: "BB-" + Date.now(),
                    pair_id: Date.now() + 1,
                    auto_pair_failed: false,
                } : {};
                const saved = Object.assign({}, payload, {
                    id: editingNewborn ? editingNewborn.id : Date.now(),
                }, mockPairData);
                if (editingNewborn) {
                    setNewborns(function(prev) {
                        return prev.map(function(nb) {
                            return nb.id === editingNewborn.id ? saved : nb;
                        });
                    });
                } else {
                    setNewborns(function(prev) { return prev.concat([saved]); });
                }
                setEditingNewborn(null);
                setForm(Object.assign({}, INITIAL_FORM));
                setApgar(JSON.parse(JSON.stringify(INITIAL_APGAR)));
                notify(actions, "success", "Newborn record saved");
                setSaving(false);
                if (onSaveSuccess) onSaveSuccess();
            }, 300);
            return;
        }

        /* ── Real API path ── */
        const request = editingNewborn
            ? axios.put(config.smsUrl  + "/obgyn/newborn/" + editingNewborn.id, payload)
            : axios.post(config.smsUrl + "/obgyn/newborn", payload);

        request
            .then(function(res) {
                /* Accept either the returned record or fall back to payload */
                const saved = (res.data && res.data.id) ? res.data : payload;
                if (editingNewborn) {
                    setNewborns(function(prev) {
                        return prev.map(function(nb) {
                            return nb.id === editingNewborn.id ? saved : nb;
                        });
                    });
                } else {
                    setNewborns(function(prev) { return prev.concat([saved]); });
                }
                setEditingNewborn(null);
                setForm(Object.assign({}, INITIAL_FORM));
                setApgar(JSON.parse(JSON.stringify(INITIAL_APGAR)));
                if (saved.auto_pair_failed) {
                    notify(actions, "warning", (editingNewborn ? "Newborn updated" : "Newborn saved") +
                        " but auto-pairing failed. Manual pairing may be needed.");
                } else if (saved.baby_patient_number) {
                    notify(actions, "success", (editingNewborn ? "Newborn updated" : "Newborn saved") +
                        " — Baby registered as Patient #" + saved.baby_patient_number);
                } else {
                    notify(actions, "success", editingNewborn ? "Newborn record updated" : "Newborn record saved");
                }
                if (onSaveSuccess) onSaveSuccess();
            })
            .catch(function(err) {
                console.error("[useNewbornRecord] saveNewborn API error:", err);
                const msg =
                    (err.response && err.response.data && err.response.data.message) ||
                    "Failed to save newborn record";
                notify(actions, "error", msg);
            })
            .finally(function() {
                setSaving(false);
            });
    }, [form, apgar, apgarTotals, deliveryRecord, editingNewborn, patient_number, actions, onSaveSuccess]);

    /* ─── Edit newborn ─── */
    const startEditNewborn = useCallback(function(nb) {
        setEditingNewborn(nb);
        setForm(Object.assign({}, INITIAL_FORM, nb));
        if (nb.apgar_details) {
            setApgar(JSON.parse(JSON.stringify(nb.apgar_details)));
        } else {
            setApgar(JSON.parse(JSON.stringify(INITIAL_APGAR)));
        }
        setActiveTab("assessment");
    }, []);

    /* ─── Cancel edit ─── */
    const cancelEdit = useCallback(function() {
        setEditingNewborn(null);
        setForm(Object.assign({}, INITIAL_FORM));
        setApgar(JSON.parse(JSON.stringify(INITIAL_APGAR)));
    }, []);

    /* ─── Alerts ─── */
    const alerts = useMemo(function() {
        const list = [];
        if (apgarTotals["5min"].complete && apgarTotals["5min"].total < 7) {
            list.push({
                type:   apgarTotals["5min"].total < 4 ? "critical" : "warning",
                title:  apgarTotals["5min"].total < 4 ? "Severe Birth Asphyxia" : "Moderate Birth Asphyxia",
                detail: "5-minute APGAR score: " + apgarTotals["5min"].total + "/10",
                action: apgarTotals["5min"].total < 4
                    ? "URGENT: Continue resuscitation. Prepare for NICU admission."
                    : "Close monitoring. May need additional resuscitation.",
            });
        }
        const w = parseFloat(form.birth_weight_grams);
        if (w && w < 2500) {
            list.push({
                type:   w < 1500 ? "critical" : "warning",
                title:  "Low Birth Weight",
                detail: "Birth weight " + w + "g (" + (weightClass ? weightClass.label : "") + ")",
                action: w < 1500
                    ? "URGENT: NICU/KMC required. Monitor temperature and feeding."
                    : "Kangaroo Mother Care. Frequent feeding. Temperature monitoring.",
            });
        }
        if (form.outcome === "fresh_stillbirth" || form.outcome === "macerated_stillbirth") {
            list.push({
                type:   "critical",
                title:  "Stillbirth",
                detail: form.outcome === "fresh_stillbirth" ? "Fresh Stillbirth" : "Macerated Stillbirth",
                action: "Document. Counsel parents. Complete stillbirth notification.",
            });
        }
        return list;
    }, [apgarTotals, form.birth_weight_grams, form.outcome, weightClass]);

    return {
        /* Constants */
        APGAR_CRITERIA,
        SEX_OPTIONS,
        RESUSCITATION_TYPES,
        CORD_CARE_OPTIONS,
        OUTCOME_OPTIONS,
        FEEDING_OPTIONS,
        /* Data */
        deliveryRecord,
        setDeliveryRecord,
        newborns,
        /* Form */
        form,
        setForm,
        handleChange,
        toggleResuscitationType,
        /* APGAR */
        apgar,
        setApgarScore,
        apgarTotals,
        getApgarColor,
        getApgarLabel,
        /* Checklist items */
        immediateCareItems,
        vaccinationItems,
        weightClass,
        /* Edit */
        editingNewborn,
        startEditNewborn,
        cancelEdit,
        /* Actions */
        saveNewborn,
        /* Alerts */
        alerts,
        /* UI */
        loading,
        saving,
        activeTab,
        setActiveTab,
    };
};